"use client";

import { useCallback, useState } from "react";

const ICON_SIZE = 14;

function Icon({ children }: { children: React.ReactNode }) {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      {children}
    </svg>
  );
}

export default function ArticleShare({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedTitleAndUrl = encodeURIComponent(`${title} — ${url}`);

  const open = useCallback((href: string) => {
    window.open(href, "_blank", "noopener,noreferrer,width=720,height=600");
  }, []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore; user can long-press to copy
    }
  }, [url]);

  const nativeShare = useCallback(async () => {
    try {
      if (typeof navigator !== "undefined" && "share" in navigator) {
        await (navigator as Navigator & { share: (data: ShareData) => Promise<void> }).share({
          title,
          url,
        });
      }
    } catch {
      // user canceled — silent
    }
  }, [title, url]);

  return (
    <div className="ba-actions" role="group" aria-label="Share this article">
      <button
        type="button"
        onClick={() => open(`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`)}
        className="ba-action-btn"
        aria-label="Share on X"
        title="Share on X"
      >
        <Icon>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
        </Icon>
      </button>
      <button
        type="button"
        onClick={() => open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`)}
        className="ba-action-btn"
        aria-label="Share on LinkedIn"
        title="Share on LinkedIn"
      >
        <Icon>
          <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12M7.12 20.45H3.56V9h3.56v11.45ZM22.22 0H1.77C.8 0 0 .78 0 1.74v20.52C0 23.22.8 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.74V1.74C24 .78 23.2 0 22.22 0" />
        </Icon>
      </button>
      <button
        type="button"
        onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`)}
        className="ba-action-btn"
        aria-label="Share on Facebook"
        title="Share on Facebook"
      >
        <Icon>
          <path d="M22 12a10 10 0 1 0-11.56 9.88V14.9H7.9V12h2.54V9.8c0-2.5 1.5-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.9h-2.33v6.98A10 10 0 0 0 22 12" />
        </Icon>
      </button>
      <button
        type="button"
        onClick={() => open(`https://wa.me/?text=${encodedTitleAndUrl}`)}
        className="ba-action-btn"
        aria-label="Share on WhatsApp"
        title="Share on WhatsApp"
      >
        <Icon>
          <path d="M20.5 3.5A11.78 11.78 0 0 0 12.02 0C5.55 0 .29 5.26.28 11.74c0 2.07.54 4.09 1.57 5.87L.18 24l6.55-1.71a11.74 11.74 0 0 0 5.29 1.34h.01c6.46 0 11.73-5.27 11.73-11.74 0-3.13-1.22-6.07-3.26-8.39M12.02 21.66h-.01a9.74 9.74 0 0 1-4.97-1.36l-.36-.21-3.89 1.02 1.04-3.8-.23-.39a9.7 9.7 0 0 1-1.48-5.18c0-5.38 4.38-9.75 9.76-9.75 2.6 0 5.05 1.02 6.89 2.86A9.7 9.7 0 0 1 21.77 12c0 5.38-4.38 9.66-9.75 9.66m5.36-7.27c-.29-.15-1.74-.86-2-.96-.27-.1-.47-.15-.66.15-.2.29-.76.95-.93 1.15-.17.2-.34.22-.63.07-.29-.15-1.24-.46-2.36-1.46a8.84 8.84 0 0 1-1.63-2.04c-.17-.29-.02-.45.13-.59.13-.13.29-.34.44-.51.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.51-.07-.15-.66-1.6-.9-2.19-.24-.57-.48-.5-.66-.51l-.56-.01c-.2 0-.51.07-.78.37-.27.29-1.02 1-1.02 2.44 0 1.44 1.05 2.84 1.2 3.04.15.2 2.07 3.17 5.02 4.44.7.3 1.25.48 1.68.62.7.22 1.34.19 1.85.12.56-.08 1.74-.71 1.98-1.4.24-.69.24-1.28.17-1.4-.07-.13-.27-.2-.56-.34" />
        </Icon>
      </button>
      <button
        type="button"
        onClick={copy}
        className={`ba-action-btn ${copied ? "ba-action-copied" : ""}`}
        aria-label={copied ? "Link copied" : "Copy link"}
        title={copied ? "Link copied" : "Copy link"}
      >
        {copied ? (
          <Icon>
            <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2Z" />
          </Icon>
        ) : (
          <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M9 14a5 5 0 0 0 7.07 0l3-3a5 5 0 1 0-7.07-7.07L11 5" />
            <path d="M15 10a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07L13 19" />
          </svg>
        )}
      </button>
      {typeof navigator !== "undefined" && "share" in navigator && (
        <button
          type="button"
          onClick={nativeShare}
          className="ba-action-btn ba-action-native"
          aria-label="More share options"
          title="More share options"
        >
          <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
          </svg>
        </button>
      )}
    </div>
  );
}
